import { useEffect, useState } from 'react'
import { createFileRoute, Outlet, Navigate, useLocation } from '@tanstack/react-router'
import { App as CapacitorApp } from '@capacitor/app'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { BottomNav } from '@/components/BottomNav'
import { DesktopShell } from '@/components/desktop/DesktopShell'
import { useIsDesktop } from '@/components/ui/use-mobile'
import { Loading } from '@/components/ui/loading'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { useOnboardingStatus } from '@/lib/hooks/onboarding'
import { useSubscriptionStatus } from '@/lib/hooks/subscription'
import { useAgreementStatus } from '@/lib/hooks/agreement'
import { useDataRetentionStatus } from '@/lib/hooks/data-retention'
import { AgreementGate } from '@/components/AgreementGate'
import { useCurrentCrewMember } from '@/hooks/useCurrentCrewMember'
import { usePushRegistration } from '@/hooks/usePushRegistration'
import { useOrpcTanstackQueryUtils } from '@/lib/orpc'

export const Route = createFileRoute('/_auth')({
  component: AuthLayout,
})

const SUBSCRIPTION_GATE_PATH = '/operations/subscriptions'

function AuthLayout() {
  const { isLoaded, isSignedIn, signOut } = useAuth()
  const { data: onboardingStatus, isLoading: isCheckingOnboarding } = useOnboardingStatus()
  const onboardingComplete = !!onboardingStatus?.completed
  const { data: subscriptionStatus, isLoading: isCheckingSubscription } = useSubscriptionStatus({
    enabled: onboardingComplete,
  })
  const { data: currentCrew, isLoading: isLoadingCrew } = useCurrentCrewMember()
  const { data: agreementStatus, isLoading: isCheckingAgreement } = useAgreementStatus()
  // Pending owner deletion request, for the "subscription expired" screen
  const { data: retentionStatus } = useDataRetentionStatus({
    enabled: subscriptionStatus?.hasSubscription === false,
  })

  // Register this device for native iOS push once we know who's signed in.
  const { pendingSwitch, confirmPendingSwitch, cancelPendingSwitch } =
    usePushRegistration((currentCrew as { id?: string } | undefined)?.id)

  const location = useLocation()
  const queryClient = useQueryClient()
  const orpcUtils = useOrpcTanstackQueryUtils()
  const isDesktop = useIsDesktop()
  const [isRefreshingSubscription, setIsRefreshingSubscription] = useState(false)

  // Re-check subscription state when the app returns to the foreground —
  // covers the renew-on-web flow where the owner completes checkout in the
  // system browser and comes back expecting the gate to release.
  useEffect(() => {
    let listener: { remove: () => void } | undefined
    void (async () => {
      listener = await CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (!isActive) return
        void queryClient.invalidateQueries({
          queryKey: orpcUtils.subscription.checkSubscription.key(),
        })
        void queryClient.invalidateQueries({
          queryKey: orpcUtils.subscription.listPurchases.key(),
        })
        // Portal links are single-use; get a fresh one after a round-trip.
        void queryClient.invalidateQueries({
          queryKey: orpcUtils.subscription.getManagementUrl.key(),
        })
      })
    })()
    return () => {
      void listener?.remove()
    }
  }, [queryClient, orpcUtils])

  // Edge case: signed in but no Crew/Company. A user can't exist without a
  // company — sign them out and bounce them to /signin so they can complete
  // signup on the web.
  const orphanedSession = isSignedIn && !isCheckingOnboarding && onboardingStatus !== undefined && !onboardingStatus?.completed
  useEffect(() => {
    if (orphanedSession) {
      signOut()
    }
  }, [orphanedSession, signOut])


  if (!isLoaded) {
    return (
      <div className="flex-1 bg-gray-50 flex items-center justify-center">
        <Loading size={48} />
      </div>
    )
  }

  if (isCheckingOnboarding || onboardingStatus === undefined) {
    return (
      <div className="flex-1 bg-gray-50 flex items-center justify-center">
        <Loading size={48} />
      </div>
    )
  }

  if (isCheckingSubscription || subscriptionStatus === undefined || isLoadingCrew) {
    return (
      <div className="flex-1 bg-gray-50 flex items-center justify-center">
        <Loading size={48} />
      </div>
    )
  }

  if (isCheckingAgreement || agreementStatus === undefined) {
    return (
      <div className="flex-1 bg-gray-50 flex items-center justify-center">
        <Loading size={48} />
      </div>
    )
  }

  // Nobody uses the app until they've accepted the current user agreement.
  if (!agreementStatus.acceptedAt) {
    return <AgreementGate version={agreementStatus.version} documents={agreementStatus.documents} />
  }

  if (!subscriptionStatus.hasSubscription) {
    if (currentCrew?.isOwner) {
      if (location.pathname !== SUBSCRIPTION_GATE_PATH) {
        return <Navigate to={SUBSCRIPTION_GATE_PATH} />
      }
    } else {
      return (
        <div className="flex-1 bg-gray-50 flex items-center justify-center p-4">
          <div className="flex flex-col gap-4 max-w-md w-full">
            <Alert>
              <AlertTitle>Company subscription has expired</AlertTitle>
              <AlertDescription>
                {subscriptionStatus.ownerEmail ? (
                  <>
                    Please contact your operations' owner at{' '}
                    <a
                      href={`mailto:${subscriptionStatus.ownerEmail}`}
                      className="font-medium underline"
                    >
                      {subscriptionStatus.ownerEmail}
                    </a>{' '}
                    to renew the subscription.
                  </>
                ) : (
                  <>Please contact your operations' owner to renew the subscription.</>
                )}
                {retentionStatus?.deletionReason === 'owner_request' && (
                  <> The owner has asked for this company and its data to be deleted.</>
                )}
              </AlertDescription>
            </Alert>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                disabled={isRefreshingSubscription}
                onClick={async () => {
                  setIsRefreshingSubscription(true)
                  try {
                    await Promise.all([
                      queryClient.invalidateQueries({
                        queryKey: orpcUtils.subscription.checkSubscription.key(),
                      }),
                      queryClient.invalidateQueries({
                        queryKey: orpcUtils.subscription.listPurchases.key(),
                      }),
                    ])
                  } finally {
                    setIsRefreshingSubscription(false)
                  }
                }}
              >
                {isRefreshingSubscription ? <Loading size={20} /> : 'Refresh Status'}
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                disabled={isRefreshingSubscription}
                onClick={() => signOut()}
              >
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      )
    }
  }

  // A tapped push notification can belong to another company the user is a
  // member of — confirm before switching the whole app over to it.
  const companySwitchDialog = (
    <AlertDialog
      open={pendingSwitch !== null}
      onOpenChange={(open) => {
        if (!open) cancelPendingSwitch()
      }}
    >
      <AlertDialogContent className="p-8">
        <AlertDialogDescription className="text-center text-sm text-content-primary-subtle leading-5">
          This notification is from {pendingSwitch?.companyName}. Switch
          company to view it?
        </AlertDialogDescription>
        <div className="flex flex-col gap-2 mt-2">
          <Button
            size="mini"
            className="w-full"
            onClick={() => void confirmPendingSwitch()}
          >
            Switch Company
          </Button>
          <Button
            variant="secondary"
            size="mini"
            className="w-full"
            onClick={cancelPendingSwitch}
          >
            Cancel
          </Button>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  )

  // Desktop (large-screen browser): left sidebar + main content, no BottomNav.
  // Touch widths (mobile/iPad) keep the existing bottom-nav layout.
  if (isDesktop) {
    return (
      <DesktopShell>
        <Outlet />
        {companySwitchDialog}
      </DesktopShell>
    )
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-1 min-h-0">
        <Outlet />
      </div>
      <BottomNav />
      {companySwitchDialog}
    </div>
  )
}
